// React
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

// Components
import Header from '@custom/Header/Header';
import Barcode from 'react-barcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Assets
import { Icon, Loader } from '@ohif/ui';
import { sevenHIllsRoundImg } from '../../../../ui/assets/images/images';
import { phoneIcon, mailIcon, mapIcon, downloadIcon, printerIcon, threeDotIcon, mailIconwhite, mapIconWhite, phoneIconWhite, signIcon, rollbackIcon, shareIcon } from '../../../../ui/assets/svgIcons/icon';
import { _reportUrls } from '@custom/Utils/constants/apiUrls';

import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { fetchRollbackReport } from '@custom/rtk-apis/report/RollbackDetail';

import './previewrollback.css';
import { _reportStatus } from '@custom/Utils/constants/appConstants';
import { capitalizeWords, customStyle, formatDays, formatTime } from '@custom/Utils/utils';
import { useTheme } from "../../../../ui/src/contextProviders"

// UI
function RollbackPreview() {

  let { id } = useParams()


  // Local state for the filtered report data
  const [reportDet, setReportDet] = useState([]);

  const dispatch = useDispatch<AppDispatch>();
  const isDarkMode = useTheme();

  const { status, result, msg, loading, error } = useSelector((state: RootState) => state.getRollbackReportSliceResponce);

  useEffect(() => {
    const fetchData = async () => {
      const response = await dispatch(fetchRollbackReport(id));
      setReportDet(response?.payload?.result);
    };
    fetchData();
  }, [dispatch, id]);

  // helpers
  const disableInputs = (html) => {
    // Add 'disabled' attribute to all input fields
    return html
      ?.replace(/<input/g, '<input disabled')
      ?.replace(/<textarea/g, '<textarea disabled')
      ?.replace(/<select/g, '<select disabled');
  };

  const printRef = useRef();

  // States
  const [showMenu, setShowMenu] = useState(false);
  const [impressions, setImpressions] = useState('');

  const printAsPdf = (action = "Print") => {
    const content = printRef.current;

    html2canvas(content, {
      scale: 2,
      useCORS: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;

      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, Math.min(pageHeight, imgHeight));
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          position,
          imgWidth,
          Math.min(pageHeight, heightLeft)
        );
        heightLeft -= pageHeight;
      }

      if (action === "Print") {
        const pdfUrl = pdf.output("bloburl");
        window.open(pdfUrl);
        pdf.autoPrint();
      } else {
        pdf.save("download.pdf");
      }
    });
  };


  const handleShare = () => {
    const content = printRef.current;

    html2canvas(content).then((canvas) => {
      canvas.toBlob((blob) => {
        const imageUrl = URL.createObjectURL(blob);
        shareOnMail(imageUrl);
      });
    });
  };



  const shareOnMail = (imageUrl) => {
    const subject = encodeURIComponent("Check out this image");
    const body = encodeURIComponent(`Here is the image: ${imageUrl}`);
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  // RollbackModal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    // Adjust age if the current month and date are before the birth month and date
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const containerStyle = {
    width: "95.7%",
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const h2Style = {
    flexGrow: 1,
    textAlign: 'center',
    fontSize: '1.875rem', // This is for 3xl size
    fontWeight: '600',
  };

  // Function to dynamically set button color based on priority
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'Routine':
        return { border: '1px solid #28a745', color: '#28a745' }; // Green
      case 'Priority':
        return { border: '1px solid #007bff', color: '#007bff' }; // Blue
      case 'Critical':
        return { border: '1px solid #FF5C5C', color: '#FF5C5C' }; // Red
      default:
        return { border: '1px solid #ccc', color: '#ccc' }; // Default grey if no priority
    }
  };

  const buttonStyle = {
    backgroundColor: 'transparent',
    padding: '5px 15px',
    borderRadius: '4px',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    ...getPriorityStyle(reportDet?.priority), // Merging dynamic styles
  };

  const dotStyle = {
    height: '8px',
    width: '8px',
    backgroundColor: buttonStyle.color,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '8px',
  };

  return (
    <>
      <div className='page page__full' style={
        isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header isBack={true} title={'Rollback Report Preview'} />

        <section className='flex items-center justify-between px-4 mt-4' style={{ display: 'flex', justifyContent: 'flex-end' }}>

          <div className='flex flex-row items-center gap-2'>
            <button onClick={() => printAsPdf("Download")} className="bg-green-500 text-black px-2 py-1 rounded border border-black flex gap-2 items-center text-[14px] font-semibold">
              <img src={downloadIcon} />
              Download
            </button>
            <button onClick={() => printAsPdf("Print")} className="bg-green-500 text-black px-2 py-1 rounded border border-black flex gap-2 items-center text-[14px] font-semibold">
              <img src={printerIcon} />
              Print
            </button>

            <div className='relative'>
              <img className='cursor-pointer p-2 mx-2' src={threeDotIcon} onClick={() => setShowMenu(!showMenu)} />

              {
                showMenu &&
                <div className="absolute p-2 bg-white" style={{ width: '120px', right: '10px', zIndex: 3, boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.19)' }}>
                  <div className='flex flex-row items-center gap-2 cursor-pointer mb-2' onClick={handleShare}>
                    <img src={shareIcon} />
                    <p className='text-[12px] font-regular'>Share</p>
                  </div>
                </div>
              }
            </div>

          </div>
        </section>
        <div className='border-b-2 border-[#49475A] pt-2 mt-2'>
        </div>
        <div ref={printRef} className='watermark-container2' style={{ overflowX: 'auto', backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : customStyle.mainBackground }} onClick={() => setShowMenu(false)}>
          <div className="px-1 content" style={{ minWidth: '1200px', color: '#49475A' }}>

            {/* Hospital info */}
            <section className="flex flex-row p-2">
              <div className="basic-1/3 p-4">
                <div className="h-full">
                  <div className='flex flex-row h-full w-full'>
                    <div className="block mx-auto" style={{ width: '150px' }}>
                      {reportDet?.hospitalDetail?.hospitalLogo ? (
                        <img src={reportDet?.hospitalDetail?.hospitalLogo} alt="Hospital Logo" className='block mx-auto' />
                      ) : (
                        <Icon name="DummyLogo"></Icon>
                      )}
                    </div>
                    <div className='border-l-2 border-[#0F8491] ml-4 h-full'></div>
                  </div>
                </div>
              </div>
              <div className="basic-2/3 p-4 w-full ml-4">
                <div>
                  <p className='text-[#0F8491] text-3xl font-semibold'>{reportDet?.hospitalDetail?.hospitalHeader?.hospitalName ? reportDet?.hospitalDetail?.hospitalHeader?.hospitalName : reportDet?.hospitalDetail?.hospitalName}</p>
                  <ul className="">
                    {reportDet?.hospitalDetail?.specialitites?.map((item, index) => (
                      <li className='text-[14px] text-[#49475A] relative pl-4' key={index}>
                        <span className="absolute left-0 top-1 text-black">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className='flex flex-row gap-4 border-t-2 border-[#0F8491] pt-2 mt-2'>
                  <div className='flex flex-row gap-2'>
                    <img src={phoneIcon} alt="" />
                    <p className='text-[14px] font-normal color-[#535353]'>{reportDet?.hospitalDetail?.hospitalHeader?.contactNumberCountryCode ? reportDet?.hospitalDetail?.hospitalHeader?.contactNumberCountryCode : reportDet?.hospitalDetail?.contactNumberCountryCode} {reportDet?.hospitalDetail?.hospitalHeader?.contactNumber ? reportDet?.hospitalDetail?.hospitalHeader?.contactNumber : reportDet?.hospitalDetail?.contactNumber}</p>
                  </div>
                  <div className='flex flex-row gap-2'>
                    <img src={mailIcon} alt="" />
                    <p className='text-[14px] font-normal color-[#535353]'>{reportDet?.hospitalDetail?.hospitalHeader?.email ? reportDet?.hospitalDetail?.hospitalHeader?.email : reportDet?.hospitalDetail?.hospitalEmailId}</p>
                  </div>
                  <div className='flex flex-row gap-2'>
                    <img src={mapIcon} alt="" />
                    <p className='text-[14px] font-normal color-[#535353]'>{reportDet?.hospitalDetail?.hospitalHeader?.address ? reportDet?.hospitalDetail?.hospitalHeader?.address + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.state + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.city + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.zipCode : reportDet?.hospitalDetail?.address + ' ' + reportDet?.hospitalDetail?.state + ' ' + reportDet?.hospitalDetail?.city + ' ' + reportDet?.hospitalDetail?.zipCode} </p>
                  </div>
                </div>

              </div>
            </section>

            <section className='bg-[#0F8491] p-2'>
              <p className='text-[17.53px] font-semibold text-white text-center'>Rollback Report</p>
            </section>
            <section className='px-2 py-4'>
              <div className="flex flex-row" style={{ columnGap: "1rem" }}>

                <div className=" flex flex-col justify-center w-1/4">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none">
                      <p className='text-[24px] font-semibold mb-4 text-[#49475A]'>
                        {reportDet?.name
                          ? reportDet.name
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                          : '---'}
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>Patient ID :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientId ? reportDet?.patientId : "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className='text-[14px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Date :</span>
                        <span className='font-normal ml-2'>{reportDet?.date ? reportDet?.date : "---"}</span>
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>Gender :</span>
                        <span className='font-normal ml-2'>{reportDet?.gender ? reportDet?.gender : "---"}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex">
                  <div className='border-l-2 border-[#0F8491] mx-2 h-full' style={{ width: "2px", backgroundColor: "#0F8491" }}></div>
                </div>

                <div className="w-1/4 flex flex-col justify-center">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none">
                      <p className='text-[14px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Age :</span>
                        <span className='font-normal ml-2'>{reportDet?.age ? `${calculateAge(reportDet?.age)} years old` : "---"}</span>
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>Blood Group :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.bloodGroup ? reportDet?.patientDetail?.bloodGroup : "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <p className='text-[14px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Phone Number :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.contactInfo?.mobileNo ? reportDet?.patientDetail?.contactInfo?.mobileNo : "---"}</span>
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>WhatsApp Number :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.contactInfo?.whatsappNo ? reportDet?.patientDetail?.contactInfo?.whatsappNo : "---"}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex">
                  <div className='border-l-2 border-[#0F8491] mx-2 h-full' style={{ width: "2px", backgroundColor: "#0F8491" }}></div>
                </div>

                <div className="w-1/2 flex flex-col justify-center">
                  <div className="flex flex-row gap-6">
                    <div className="flex-none flex flex-col justify-center">
                      <p className='text-[14px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Height :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.height ? reportDet?.patientDetail?.height + ' ' + 'cm' : "---"}</span>
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>Weight :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.weight ? reportDet?.patientDetail?.weight + ' ' + 'kg' : "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <p className='text-[14px] font-semibold mb-4'>
                        <span className='text-[#49475A]'>Consulting Doctor :</span>
                        <span className='font-normal ml-2'>{reportDet?.patientDetail?.referPhysicianInfo?.name ? reportDet?.patientDetail?.referPhysicianInfo?.name : "---"}</span>
                      </p>
                      <p className='text-[14px] font-semibold'>
                        <span className='text-[#49475A]'>Modality Type :</span>
                        <span className='font-normal ml-2'>{reportDet?.modality ? reportDet?.modality : "---"}</span>
                      </p>
                    </div>
                    <div className="flex-1 text-center items-center flex flex-col justify-center">
                      <p className='text-[14px] font-semibold mb-1'>
                        <span className='text-[#49475A]'>UHID :</span>
                        <span className='font-normal ml-2'>{reportDet?.uhid ? reportDet?.uhid : "---"}</span>
                      </p>
                      {reportDet?.uhid && <Barcode displayValue={false} width={1.2} height={50} value={reportDet?.uhid} />}
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div style={containerStyle}>
              <h2 style={h2Style}>{reportDet?.reportName || '---'}</h2>
              <button style={buttonStyle}>
                <span style={dotStyle}></span>
                {reportDet?.priority}
              </button>
            </div>
            <section
              id="html-render"
              className='mb-4'
              dangerouslySetInnerHTML={{
                __html: `
                    <h3 class="text-xl font-semibold mb-2 font-sans">Findings</h3>
                    ${disableInputs(reportDet?.templateHTML) || ''}
                  `,
              }}
            >
            </section>

            {(reportDet?.status === "Draft" || reportDet?.status === "Completed" || reportDet?.status === "Sent for Review") && (
              <div className="mb-4">
                <label htmlFor="comment" className="block font-bold mb-2">Comment (For internal use only)</label>
                <textarea
                  id="comment"
                  rows={3}
                  cols={105}
                  disabled
                  value={reportDet?.comment}
                  className=""
                ></textarea>
              </div>
            )}

            {reportDet?.impressions ? (
              <div>
                <h3 className="text-xl font-semibold mb-2 font-sans">Impression</h3>
                <textarea
                  id='impression'
                  className="w-full p-2 border border-gray-300 rounded bg-transparent"
                  rows={4}
                  value={reportDet?.impressions}
                  onChange={(e) => setImpressions(e.target.value)}
                ></textarea>
              </div>
            ) : null}

            {(reportDet?.status === _reportStatus.Authorized) && (
              <>
                <section className='mb-4'>
                  <div className='border-b-2 border-[#49475A] pt-2 mt-2 mb-4' style={{ marginTop: '20px', color: '#49475A' }}>
                  </div>
                  <img src={reportDet?.signatureDetail?.signature} alt="Signature" />
                </section>

                <section className='mb-2 border-b-2 border-[#49475A] pt-2 mt-2 mb-4' style={{ color: '#49475A' }}>
                  <p className="text-[22px] font-semibold">{capitalizeWords(reportDet?.signatureDetail?.name)}</p>
                  {reportDet?.signatureDetail?.licenseNumber && <p className='text-[16px] font-normal'><span className='font-bold'>License Number -</span> {reportDet?.signatureDetail?.licenseNumber}</p>}
                  {reportDet?.signatureDetail?.specialization && reportDet?.signatureDetail?.specialization.length > 0 && <p className='text-[16px] font-normal'><span className='font-bold'>Specialization - </span>{reportDet?.signatureDetail?.specialization.join(", ")}</p>}
                  {reportDet?.signatureDetail?.availabilityDay?.length > 0 && (
                    <p className='text-[16px] font-normal'>
                      <span className='font-bold'>Availability Day - </span>
                      {formatDays(reportDet?.signatureDetail?.availabilityDay)}
                      {reportDet?.signatureDetail?.fromTimeAvailability && reportDet?.signatureDetail?.toTimeAvailability
                        ? `, ${formatTime(reportDet.signatureDetail.fromTimeAvailability, true)} - ${formatTime(reportDet.signatureDetail.toTimeAvailability, true)}`
                        : null}
                    </p>
                  )}
                </section>
              </>
            )}

            <div className='bg-[#0F8491] pt-2 pb-2 pr-2 pl-2'>
              <div className='flex flex-row gap-4 justify-between items-center border-t-2 border-[#0F8491]'>
                <div className='flex flex-row gap-2'>
                  <img src={phoneIconWhite} alt="" />
                  <p className='text-[14px] font-normal text-white'>{reportDet?.hospitalDetail?.hospitalFooter?.contactNumberCountryCode ? reportDet?.hospitalDetail?.hospitalFooter?.contactNumberCountryCode : reportDet?.hospitalDetail?.contactNumberCountryCode} {reportDet?.hospitalDetail?.hospitalFooter?.contactNumber ? reportDet?.hospitalDetail?.hospitalFooter?.contactNumber : reportDet?.hospitalDetail?.contactNumber}</p>
                </div>
                <div className='flex flex-row gap-2'>
                  <img src={mailIconwhite} alt="" />
                  <p className='text-[14px] font-normal text-white'>{reportDet?.hospitalDetail?.hospitalFooter?.email ? reportDet?.hospitalDetail?.hospitalFooter?.email : reportDet?.hospitalDetail?.hospitalEmailId}</p>
                </div>
                <div className='flex flex-row gap-2'>
                  <img src={mapIconWhite} alt="" />
                  <p className='text-[14px] font-normal text-white'>{reportDet?.hospitalDetail?.hospitalHeader?.address ? reportDet?.hospitalDetail?.hospitalHeader?.address + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.state + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.city + ' ' + reportDet?.hospitalDetail?.hospitalHeader?.zipCode : reportDet?.hospitalDetail?.address + ' ' + reportDet?.hospitalDetail?.state + ' ' + reportDet?.hospitalDetail?.city + ' ' + reportDet?.hospitalDetail?.zipCode}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={loading}></Loader>
    </>
  );
}

export default RollbackPreview
